import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CircleCheck,
  CircleX,
  CloudUpload,
  Download,
  FileSpreadsheet,
  FolderOpen,
  ListChecks,
  Loader2,
  type LucideIcon,
  RefreshCw,
  ScanEye,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { useUserSitesWithCTEIds } from "@/api/auth/sites";
import {
  exportEquipmentExcel,
  useGetEquipmentSummary,
} from "@/api/client-equipment";
import { PageStatus } from "@/components/page-status";
import { useEquipmentImportFlow } from "@/components/sites/equipment-import-dialog/use-equipment-import-flow";
import {
  type CheckResult,
  CHECK_ROW_STATUS_LABEL,
  deriveCheckResult,
} from "@/components/sites/equipment-upload/derive-check-result";
import { completedAttempts } from "@/components/sites/equipment-upload/attempts";
import { PreviousUploads } from "@/components/sites/equipment-upload/previous-uploads";
import { UploadCheckResults } from "@/components/sites/equipment-upload/upload-check-results";
import { UploadSuccess } from "@/components/sites/equipment-upload/upload-success";
import { Button } from "@/components/ui/button";
import {
  FloatingActionBar,
  FloatingActionBarDivider,
} from "@/components/ui/floating-action-bar";
import { useAuth } from "@/hooks/use-auth";
import { formatFileSize } from "@/lib/utils";
import { useRegisterOnboardingReset } from "@/components/sites/onboarding-reset";
import { useSiteStore } from "@/stores/site-store";

export const Route = createFileRoute("/dashboard/onboarding/equipment-upload")({
  component: EquipmentUploadPage,
  staticData: {
    title: "Equipment upload",
  },
});

/** Mirrors the size ceiling stated on the drop zone. */
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const ACCEPTED_TYPES = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.ms-excel": [".xls"],
};

/**
 * The three things an engineer needs to know before handing over a register.
 * They set the expectation that the import is lossless and that the sheet —
 * not this screen — is where values get corrected.
 */
const WHAT_HAPPENS: { icon: LucideIcon; title: string; description: string }[] =
  [
    {
      icon: ListChecks,
      title: "Everything is imported",
      description:
        "No row is rejected. Values we can't recognise are stored exactly as they appear in your sheet.",
    },
    {
      icon: ScanEye,
      title: "You see the read first",
      description:
        "After upload you get a read-only summary of what was understood, tag by tag, before you commit.",
    },
    {
      icon: RefreshCw,
      title: "Corrections happen in the sheet",
      description:
        "To change a value, fix the spreadsheet and replace the file — nothing is edited here.",
    },
  ];

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Renders the check as a spreadsheet the engineer can work through beside the
 * source file. There is no server-side report endpoint — the dry-run result is
 * already in hand, so the CSV is assembled here.
 */
function downloadCheckReport(fileName: string, result: CheckResult) {
  const lines = [
    ["Row", "Equipment tag", "Status", "Field", "Detail"],
    ...result.fileIssues.map((issue) => [
      "",
      "",
      "File",
      issue.fieldName,
      issue.message,
    ]),
    ...result.rows.flatMap((row) =>
      row.issues.map((issue) => [
        String(row.rowNumber),
        row.tag ?? "",
        CHECK_ROW_STATUS_LABEL[row.status],
        issue.fieldName,
        issue.message,
      ]),
    ),
  ].map((cells) => cells.map(csvCell).join(","));

  const blob = new Blob([lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName.replace(/\.[^.]+$/, "")}-check.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function EquipmentUploadPage() {
  const { selectedSiteId } = useSiteStore();
  const { canAccess } = useAuth();
  // Only for the access guards below — the asset itself is named in the
  // onboarding layout's header.
  const { isLoading } = useUserSitesWithCTEIds();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  /** Set when a past upload is opened from the drop screen's history list. */
  const [viewedAttemptId, setViewedAttemptId] = useState<string | null>(null);

  const {
    uploads,
    step,
    errorMessage,
    currentAttempt,
    startImport,
    confirmImport,
    reset,
  } = useEquipmentImportFlow(selectedSiteId ?? "");

  // "Register total now" on the receipt. Kept out of the import flow because it
  // counts the whole register, not this upload.
  const { data: equipmentSummary } = useGetEquipmentSummary(
    { siteId: selectedSiteId ?? undefined },
    { query: { enabled: !!selectedSiteId } },
  );

  // Picking a new file at any point restarts the flow: the previous attempt's
  // results describe a file that is no longer on screen.
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      reset();
      setViewedAttemptId(null);
      setSelectedFile(file);
    },
    [reset],
  );

  // react-dropzone only reports rejections through this callback — without it a
  // too-large or wrong-type file is silently ignored, which reads as a broken
  // drop target.
  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const reason = rejections[0]?.errors[0]?.code;
    toast.error(
      reason === "file-too-large"
        ? "That file is over 25 MB. Split the register or remove unused sheets."
        : "That file isn't a spreadsheet. Upload an .xlsx or .xls file.",
    );
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPTED_TYPES,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_FILE_BYTES,
  });

  const downloadTemplate = async () => {
    try {
      const blob = await exportEquipmentExcel();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "equipment-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  // The receipt is shown for the import that just finished, or for whichever
  // past upload was opened from the history list.
  const viewedAttempt = viewedAttemptId
    ? (completedAttempts(uploads).find((a) => a.id === viewedAttemptId) ?? null)
    : null;
  const successAttempt = step === "DONE" ? currentAttempt : viewedAttempt;

  const result = useMemo(
    () => (currentAttempt ? deriveCheckResult(currentAttempt) : null),
    [currentAttempt],
  );
  const successResult = useMemo(
    () => (successAttempt ? deriveCheckResult(successAttempt) : null),
    [successAttempt],
  );

  const fileName =
    selectedFile?.name ?? currentAttempt?.fileName ?? "your file";

  // A dry run that produced no importable record is a file-level failure — the
  // wrong sheet, the wrong template, or nothing the parser could place — so it
  // gets an explanation rather than an empty results table.
  const isReviewing = step === "RESULTS" || step === "IMPORTING";
  const checkFailed = isReviewing && (result?.importable ?? 0) === 0;
  const showResults = isReviewing && !checkFailed;
  const isBusy = step === "UPLOADING" || step === "VALIDATING";
  const showSuccess = !!successAttempt && !!successResult;
  // The drop zone owns the page only while nothing else is on it.
  const showDropZone = step === "PICK_FILE" && !showSuccess;

  const startOver = useCallback(() => {
    reset();
    setViewedAttemptId(null);
    setSelectedFile(null);
  }, [reset]);

  // Clicking "Equipment sheet" in the step rail while a receipt is on screen
  // returns to the drop zone. Not offered while a sheet is uploading or being
  // checked — that click would throw away work in flight.
  useRegisterOnboardingReset(showSuccess ? startOver : null);

  // The attempt row keeps changing identity as the list refetches, so the
  // completion toast is fired off the step transition rather than the payload.
  const importedCount = currentAttempt?.successCount ?? 0;
  const hasToastedImport = useRef(false);
  useEffect(() => {
    if (step !== "DONE") {
      hasToastedImport.current = false;
      return;
    }
    if (hasToastedImport.current) return;
    hasToastedImport.current = true;
    toast.success(
      `${importedCount} row${importedCount === 1 ? "" : "s"} imported`,
    );
  }, [step, importedCount]);

  if (!isLoading && !selectedSiteId) {
    return (
      <PageStatus
        variant="empty"
        title="No asset selected"
        description="Pick an asset in the workspace switcher before uploading a register — every equipment tag is imported against one asset."
      />
    );
  }

  // Importing a register is manager-floor server-side (admin bypasses via the
  // role hierarchy). Show the wall rather than a drop zone that would 403.
  if (!isLoading && !canAccess("manager", selectedSiteId ?? undefined)) {
    return (
      <PageStatus
        variant="not-found"
        title="You can't upload a register for this asset"
        description="Importing an equipment register needs manager access. Ask a site manager or an administrator to run the upload."
        actions={
          <Button
            variant="outline"
            nativeButton={false}
            render={<Link to="/dashboard/equipment" />}
          >
            Back to equipment
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6 md:gap-8">
        {/* The asset and its onboarding status are in the layout header; this
            only has to introduce the screen. */}
        <header className="min-w-0">
          <h2 className="text-lg font-semibold tracking-[-0.005em]">
            Equipment upload
          </h2>
          {!showSuccess && (
            <p className="text-muted-foreground mt-0.5 text-sm">
              {showDropZone
                ? "Import an EEHA register so documents have equipment tags to link to."
                : "Read-only check before importing."}
            </p>
          )}
        </header>

        {/* The file input lives outside the drop plate so the FAB's "Replace
            file" can still call open() once the plate is gone. */}
        <input {...getInputProps()} />

        {showDropZone && (
          <>
            {/* Drop target doubles as the staged-file panel — same plate, so the
                page doesn't reflow when a file is picked. */}
            <div
              {...getRootProps()}
              className={`bg-muted flex min-h-[19rem] cursor-pointer flex-col items-center justify-center gap-3 rounded-[10px] border px-6 py-8 text-center transition-colors ${
                isDragActive ? "border-foreground/30 bg-muted/60" : ""
              }`}
            >
              <div className="bg-background flex size-11 items-center justify-center rounded-full border">
                {selectedFile ? (
                  <FileSpreadsheet
                    className="text-muted-foreground size-5"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                ) : (
                  <CloudUpload
                    className="text-muted-foreground size-5"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                )}
              </div>

              {selectedFile ? (
                <>
                  <p className="max-w-full truncate text-lg font-medium">
                    {selectedFile.name}
                  </p>
                  {/* No buttons here — the staged file's actions (Replace,
                      Upload and check) live in the action bar. The plate itself
                      still opens the picker on click or drop. */}
                  <p className="text-muted-foreground text-sm">
                    {formatFileSize(selectedFile.size)} · ready to check
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium">
                    {isDragActive
                      ? "Drop it here"
                      : "Drop your equipment sheet here"}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    or pick one from your computer
                  </p>
                  <Button variant="outline" className="gap-2">
                    <FolderOpen className="size-4" aria-hidden />
                    Browse files
                  </Button>
                </>
              )}

              <p className="text-muted-foreground text-xs">
                Accepts .xlsx and .xls · up to 25 MB · one sheet at a time
              </p>
            </div>

            <PreviousUploads uploads={uploads} onSelect={setViewedAttemptId} />

            <div className="grid gap-4 @2xl/main:grid-cols-3">
              {WHAT_HAPPENS.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="flex flex-col gap-2 rounded-[10px] border px-4.5 py-4"
                >
                  <Icon
                    className="text-muted-foreground size-[18px]"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                  <p className="text-sm font-semibold">{title}</p>
                  <p className="text-muted-foreground text-sm">{description}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {isBusy && (
          <div className="bg-muted flex min-h-[19rem] flex-col items-center justify-center gap-3 rounded-[10px] border px-6 py-8 text-center">
            <div className="bg-background flex size-11 items-center justify-center rounded-full border">
              <Loader2
                className="text-muted-foreground size-5 animate-spin"
                strokeWidth={1.75}
                aria-hidden
              />
            </div>
            <p className="max-w-full truncate text-lg font-medium">
              {fileName}
            </p>
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {step === "UPLOADING"
                ? "Uploading — don't close this page."
                : "Reading the sheet. Nothing is imported yet."}
            </p>
          </div>
        )}

        {step === "ERROR" && (
          <div className="flex flex-col items-center gap-3 rounded-[10px] border px-6 py-12 text-center">
            <div className="bg-destructive/10 text-destructive flex size-11 items-center justify-center rounded-full">
              <CircleX className="size-5" strokeWidth={1.75} aria-hidden />
            </div>
            <p className="text-lg font-medium">We couldn't check that file</p>
            <p className="text-muted-foreground max-w-lg text-sm">
              {errorMessage ??
                "Something went wrong before the sheet could be read. Try again, or replace the file."}
            </p>
          </div>
        )}

        {checkFailed && result && (
          <div className="flex flex-col items-center gap-3 rounded-[10px] border px-6 py-12 text-center">
            <div className="bg-destructive/10 text-destructive flex size-11 items-center justify-center rounded-full">
              <CircleX className="size-5" strokeWidth={1.75} aria-hidden />
            </div>
            <p className="text-lg font-medium">
              Nothing in this file can be imported
            </p>
            <p className="text-muted-foreground max-w-lg text-sm">
              {result.rowsRead === 0
                ? "No equipment rows were found in the sheet. Check you're uploading the register itself and not a cover page, then replace the file."
                : `All ${result.rowsRead} rows were read, but none carry an equipment tag — the key every document links to. Add the tag column to the sheet and replace the file.`}
            </p>
            {result.fileIssues.length > 0 && (
              <ul className="text-muted-foreground max-w-lg list-none text-sm">
                {result.fileIssues.map((issue) => (
                  <li key={`${issue.fieldName}-${issue.errorCode}`}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {showSuccess && successAttempt && successResult && (
          <UploadSuccess
            attempt={successAttempt}
            result={successResult}
            registerTotal={equipmentSummary?.totalCount ?? null}
            onImportAnother={startOver}
          />
        )}

        {showResults && result && (
          <UploadCheckResults
            fileName={fileName}
            fileSize={selectedFile?.size ?? null}
            result={result}
          />
        )}
      </div>

      <FloatingActionBar visible>
        {showDropZone && (
          <>
            {/* No Cancel: leaving this screen is navigation, and the step rail
                above already offers every way out. */}
            <Button
              variant="ghost"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
              onClick={() => {
                void downloadTemplate();
              }}
            >
              <Download className="size-4" aria-hidden />
              Download template
            </Button>
            <FloatingActionBarDivider />
            <Button
              variant="ghost"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
              onClick={open}
            >
              <FolderOpen className="size-4" aria-hidden />
              {selectedFile ? "Replace file" : "Select file"}
            </Button>
            {selectedFile && (
              <Button
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
                onClick={() => {
                  void startImport(selectedFile);
                }}
              >
                <ShieldCheck className="size-4" aria-hidden />
                Upload and check
              </Button>
            )}
          </>
        )}

        {isBusy && (
          <Button
            disabled
            className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
          >
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {step === "UPLOADING" ? "Uploading…" : "Checking rows…"}
          </Button>
        )}

        {(step === "RESULTS" || step === "IMPORTING") && result && (
          <>
            {result.rows.length > 0 && (
              <>
                <Button
                  variant="ghost"
                  className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
                  onClick={() => {
                    downloadCheckReport(fileName, result);
                  }}
                >
                  <Download className="size-4" aria-hidden />
                  Download report
                </Button>
                <FloatingActionBarDivider />
              </>
            )}
            <Button
              variant="ghost"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
              onClick={open}
              disabled={step === "IMPORTING"}
            >
              <FolderOpen className="size-4" aria-hidden />
              Replace file
            </Button>
            {result.importable > 0 && (
              <Button
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
                onClick={() => {
                  void confirmImport();
                }}
                disabled={step === "IMPORTING"}
              >
                {step === "IMPORTING" ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <CircleCheck className="size-4" aria-hidden />
                )}
                {step === "IMPORTING"
                  ? "Importing…"
                  : `Import ${result.importable} row${
                      result.importable === 1 ? "" : "s"
                    }`}
              </Button>
            )}
          </>
        )}

        {step === "ERROR" && (
          <Button
            className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
            onClick={open}
          >
            <FolderOpen className="size-4" aria-hidden />
            Replace file
          </Button>
        )}

        {showSuccess && successResult && (
          <>
            {successResult.rows.length > 0 && (
              <>
                <Button
                  variant="ghost"
                  className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground gap-2"
                  onClick={() => {
                    downloadCheckReport(
                      successAttempt?.fileName ?? fileName,
                      successResult,
                    );
                  }}
                >
                  <Download className="size-4" aria-hidden />
                  Download report
                </Button>
                <FloatingActionBarDivider />
              </>
            )}
            {/* Both actions act on this page. Where to go next is the step
                rail's job, not the bar's. */}
            <Button
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2"
              onClick={startOver}
            >
              <FileSpreadsheet className="size-4" aria-hidden />
              Import another sheet
            </Button>
          </>
        )}
      </FloatingActionBar>
    </>
  );
}
